import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import Visibility from '@mui/icons-material/VisibilityOutlined'
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined'
import { LoadingButton } from '@mui/lab'
import Box from '@mui/material/Box'
import { AuthFormBox } from './__styled'
import { Input } from '@/components'
import { validateEmail, validatePassword, validatePhoneNumber } from '@/utils/helper'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import { useTheme } from '@mui/material'

interface FormProps {
  error: string
  loading: boolean
  onSubmit: (user: string, password: string) => void
}

export default function SignUpForm1({ error, loading, onSubmit }: FormProps) {
  const [user, setUser] = useState('') // Email or Phone number
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [editing, setEditing] = useState(false)
  const theme = useTheme()

  const formError = useMemo(() => {
    const error: Record<string, string> = {}
    if (!user.length) {
      error.user = 'Email or Phone Number is required'
    } else if (!validateEmail(user) && !validatePhoneNumber(user)) {
      error.user = 'Enter Valid Phone/Email'
    }
    if (!password.length) {
      error.password = 'Password is required'
    } else if (password.length < 8) {
      error.password = 'Password is too short - should be 8 chars minimum.'
    } else if (!validatePassword(password)) {
      error.password =
        'Password can only contain Latin Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character.'
    }
    if (password !== confirmPassword) {
      error.confirm = 'Passwords must match'
    }
    return error
  }, [user, password, confirmPassword])

  const isFormValid = !formError.user && !formError.password && !formError.confirm

  const onChangeUser = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser(e.target.value)
    setEditing(true)
  }

  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    setEditing(true)
  }

  const onChangeConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value)
    setEditing(true)
  }

  const onContinue = () => {
    if (isFormValid) onSubmit(user, password)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        p: { xs: 3, sm: 4, md: 6 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '440px' }}>
        {/* Header */}
        <Box mb={4}>
          <Typography
            variant='h4'
            fontWeight={700}
            color='#0f172a'
            mb={1.5}
            sx={{
              fontSize: { xs: '32px', md: '36px' },
              letterSpacing: '-0.02em',
            }}
          >
            Create Your Account
          </Typography>
          <Typography variant='body1' color='#64748b' lineHeight={1.6} sx={{ fontSize: '16px' }}>
            Join 10,000+ creators designing stunning patterns
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Input
            value={user}
            placeholder='Email or Phone Number'
            onChange={onChangeUser}
            errorText={formError.user}
            isError={editing && !!formError.user}
            name='email'
          />
          <Input
            value={password}
            placeholder='Create Password'
            onChange={onChangePassword}
            rightIcon={<SVG src={showPassword ? SVGS.VisibilityOffIcon : SVGS.VisibilityIcon} />}
            onToggleIcon={() => setShowPassword(!showPassword)}
            type={showPassword ? 'text' : 'password'}
            errorText={formError.password}
            isError={editing && !!formError.password}
            name='password'
          />
          <Input
            value={confirmPassword}
            placeholder='Confirm Password'
            onChange={onChangeConfirmPassword}
            rightIcon={
              <SVG src={showConfirmPassword ? SVGS.VisibilityOffIcon : SVGS.VisibilityIcon} />
            }
            onToggleIcon={() => setShowConfirmPassword(!showConfirmPassword)}
            type={showConfirmPassword ? 'text' : 'password'}
            errorText={formError.confirm}
            isError={editing && !!formError.confirm}
            name='confirm password'
          />

          {/* Error Message */}
          {!loading && !!error && (
            <Box
              sx={{
                p: 1.5,
                backgroundColor: '#fee',
                borderRadius: '8px',
                border: '1px solid #fcc',
              }}
            >
              <Typography variant='body2' color='error.main' fontSize='14px'>
                {error}
              </Typography>
            </Box>
          )}

          {/* Continue Button */}
          <LoadingButton
            variant='contained'
            size='large'
            fullWidth
            disabled={!isFormValid}
            onClick={onContinue}
            loading={loading}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
              letterSpacing: '-0.01em',
              py: '14px',
              mt: 1,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5558e3 0%, #7c4ee8 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
              '&:disabled': {
                background: '#e2e8f0',
                color: '#94a3b8',
                boxShadow: 'none',
              },
            }}
          >
            Continue
          </LoadingButton>

          {/* Sign In Link */}
          <Box display='flex' justifyContent='center' alignItems='center' gap={0.5} mt={2}>
            <Typography variant='body2' color='#64748b' fontSize='15px'>
              Already have an account?
            </Typography>
            <Link to='/login' style={{ textDecoration: 'none' }}>
              <Typography
                variant='body2'
                sx={{
                  color: '#6366f1',
                  fontWeight: 600,
                  fontSize: '15px',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#5558e3',
                  },
                }}
              >
                Sign In
              </Typography>
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
